/**
 * Heatmap tracking script for Optibase
 * This script tracks user interactions (scroll, taps/clicks, mouse movements)
 * and sends the data to the Optibase API for heatmap visualization.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
function OptibaseHeatmap() {
    if (window.optibaseHeatmapInitialized) {
        return;
    }
    window.optibaseHeatmapInitialized = true;
    var publicApiKey = null;
    var baseApiUrl = null;
    var heatmapData = {
        scroll: [],
        taps: [],
        movement: [],
    };
    var pageStartTime = Date.now();
    var lastScrollTime = 0;
    var scrollThrottleDelay = 100; // Throttle scroll events to every 100ms
    var movementThrottleDelay = 50; // Throttle movement events to every 50ms
    var lastMovementTime = 0;
    var lastRecordedScrollDepth = 0;
    var hasRecordedPageview = false;
    var isSendingData = false; // Prevent concurrent sends
    var heatmapEnabledVariants = [];
    // Touch tracking to distinguish taps from scrolls
    var touchStartX = null;
    var touchStartY = null;
    var touchStartTime = null;
    var TAP_DISTANCE_THRESHOLD = 15; // Max pixels moved to count as a tap
    var TAP_DURATION_THRESHOLD = 500; // Max milliseconds for a tap
    // Configuration constants
    var SEND_INTERVAL_MS = 60000; // Send data every 60 seconds (reduced from 30s to lower server load)
    var MAX_DATA_POINTS_PER_TYPE = 1000; // Maximum data points per type before forcing a send
    var MAX_TOTAL_DATA_POINTS = 2500; // Maximum total data points before forcing a send
    var MIN_SCROLL_DEPTH_DELTA = 0.01; // Require at least 1% deeper scroll before logging again
    function log(scope) {
        var messages = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            messages[_i - 1] = arguments[_i];
        }
        var debugMode = Boolean(localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem('optibaseDebugMode'));
        if (debugMode) {
            console.log.apply(console, __spreadArray(["[Optibase Heatmap ".concat(scope, "]:")], messages, false));
        }
    }
    function getCurrentDevice() {
        var _a;
        if (typeof window === 'undefined') {
            return 'DESKTOP';
        }
        var width = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0, ((_a = window.screen) === null || _a === void 0 ? void 0 : _a.width) || 0);
        if (width >= 1200) {
            return 'DESKTOP';
        }
        if (width >= 600) {
            return 'TABLET';
        }
        return 'MOBILE';
    }
    function getCurrentScript() {
        return document.currentScript;
    }
    function initialize() {
        var _a;
        var currentScript = getCurrentScript();
        if (!currentScript) {
            console.error('[Optibase Heatmap] Script not found');
            return;
        }
        publicApiKey =
            (_a = currentScript.getAttribute('public-api-key')) !== null && _a !== void 0 ? _a : currentScript.getAttribute('data-public-api-key');
        if (!publicApiKey) {
            console.error('[Optibase Heatmap] Missing public-api-key attribute in script tag.');
            return;
        }
        var currentScriptSrc = currentScript.getAttribute('src');
        if (!currentScriptSrc) {
            console.error('[Optibase Heatmap] Missing src attribute in script tag.');
            return;
        }
        try {
            var scriptUrl = new URL(currentScriptSrc);
            baseApiUrl = "".concat(scriptUrl.protocol, "//").concat(scriptUrl.hostname).concat(scriptUrl.port ? ':' + scriptUrl.port : '');
        }
        catch (_b) {
            console.error('[Optibase Heatmap] Invalid script src URL.');
            return;
        }
        log('initialize', "Initialized with publicApiKey: ".concat(publicApiKey));
        syncActiveVariants();
        registerVariantListener();
        startTracking();
    }
    function filterHeatmapVariants(variants) {
        if (!Array.isArray(variants)) {
            return [];
        }
        return variants.filter(function (variant) {
            return variant && variant.userFriendlyTestId && variant.userFriendlyVariantId;
        });
    }
    function syncActiveVariants() {
        heatmapEnabledVariants = filterHeatmapVariants(window.optibaseActiveVariants);
    }
    function registerVariantListener() {
        var addListener = window.optibaseAddActiveVariantsListener;
        if (typeof addListener === 'function') {
            addListener(function (variants) {
                heatmapEnabledVariants = filterHeatmapVariants(variants);
            });
        }
    }
    function getTrackedVariants() {
        if (heatmapEnabledVariants.length > 0) {
            return heatmapEnabledVariants;
        }
        syncActiveVariants();
        return heatmapEnabledVariants;
    }
    function getManualHeatmapUrls() {
        var urls = window.optibaseManualHeatmapUrls;
        return Array.isArray(urls) ? urls : [];
    }
    function normalizePageUrl(url) {
        if (!url)
            return null;
        try {
            var parsed = new URL(url);
            parsed.protocol = 'https:';
            if (parsed.hostname.startsWith('www.')) {
                parsed.hostname = parsed.hostname.slice(4);
            }
            return parsed.origin + parsed.pathname;
        }
        catch (_a) {
            try {
                var parsed = new URL(url, window.location.origin);
                parsed.protocol = 'https:';
                if (parsed.hostname.startsWith('www.')) {
                    parsed.hostname = parsed.hostname.slice(4);
                }
                return parsed.origin + parsed.pathname;
            }
            catch (_b) {
                return null;
            }
        }
    }
    function getStandaloneMatchesForCurrentUrl() {
        var normalizedCurrent = normalizePageUrl(window.location.origin + window.location.pathname);
        if (!normalizedCurrent) {
            return [];
        }
        return getManualHeatmapUrls().filter(function (url) {
            var normalized = normalizePageUrl(url);
            return normalized && normalized === normalizedCurrent;
        });
    }
    function clamp01(value) {
        if (Number.isNaN(value)) {
            return 0;
        }
        return Math.max(0, Math.min(1, value));
    }
    function normalizeCoordinates(x, y) {
        var scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        var scrollY = window.pageYOffset || document.documentElement.scrollTop;
        var scrollWidth = document.documentElement.scrollWidth ||
            document.body.scrollWidth ||
            window.innerWidth;
        var scrollHeight = document.documentElement.scrollHeight ||
            document.body.scrollHeight ||
            window.innerHeight;
        // Prevent division by zero
        if (scrollWidth === 0 || scrollHeight === 0) {
            return { x: 0, y: 0 };
        }
        // Normalize to 0-1 range based on document dimensions
        var normalizedX = (x + scrollX) / scrollWidth;
        var normalizedY = (y + scrollY) / scrollHeight;
        return {
            x: Math.max(0, Math.min(1, normalizedX)),
            y: Math.max(0, Math.min(1, normalizedY)),
        };
    }
    // Check if we need to send data due to size limits
    function checkDataLimits() {
        // Don't check if we're already sending data
        if (isSendingData) {
            return;
        }
        var totalPoints = heatmapData.scroll.length +
            heatmapData.taps.length +
            heatmapData.movement.length;
        var exceedsPerTypeLimit = heatmapData.scroll.length >= MAX_DATA_POINTS_PER_TYPE ||
            heatmapData.taps.length >= MAX_DATA_POINTS_PER_TYPE ||
            heatmapData.movement.length >= MAX_DATA_POINTS_PER_TYPE;
        var exceedsTotalLimit = totalPoints >= MAX_TOTAL_DATA_POINTS;
        if (exceedsPerTypeLimit || exceedsTotalLimit) {
            log('checkDataLimits', 'Data limits reached, forcing send', {
                scroll: heatmapData.scroll.length,
                taps: heatmapData.taps.length,
                movement: heatmapData.movement.length,
                total: totalPoints,
            });
            // Call async function without awaiting (fire and forget)
            // The isSendingData flag will prevent concurrent calls
            sendHeatmapData().catch(function (error) {
                console.error('[Optibase Heatmap] Error in checkDataLimits send:', error);
            });
        }
    }
    function trackScroll() {
        var now = Date.now();
        if (now - lastScrollTime < scrollThrottleDelay) {
            return;
        }
        lastScrollTime = now;
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var scrollWidth = document.documentElement.scrollWidth ||
            document.body.scrollWidth ||
            window.innerWidth;
        var scrollHeight = document.documentElement.scrollHeight ||
            document.body.scrollHeight ||
            window.innerHeight;
        var viewportHeight = window.innerHeight;
        // Prevent division by zero
        if (scrollHeight === 0) {
            return;
        }
        // Calculate scroll depth as percentage
        var scrollDepth = clamp01((scrollTop + viewportHeight) / scrollHeight);
        // Only record when the user reaches a new section of the page
        if (scrollDepth <= lastRecordedScrollDepth + MIN_SCROLL_DEPTH_DELTA) {
            return;
        }
        lastRecordedScrollDepth = scrollDepth;
        var normalizedX = 0.5; // Center of the viewport for compatibility
        heatmapData.scroll.push({
            x: normalizedX,
            y: scrollDepth,
            timestamp: now - pageStartTime,
            docWidth: scrollWidth,
            docHeight: scrollHeight,
        });
        log('trackScroll', "Scroll depth: ".concat(scrollDepth.toFixed(2)));
        // Check if we need to send data due to limits
        checkDataLimits();
    }
    function trackTouchStart(event) {
        var _a;
        var touch = (_a = event.touches) === null || _a === void 0 ? void 0 : _a[0];
        if (!touch)
            return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchStartTime = Date.now();
    }
    function trackTap(event) {
        var _a, _b;
        var x, y;
        if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
            // For touchend events, touches is empty, use changedTouches instead
            var touch = ((_a = event.changedTouches) === null || _a === void 0 ? void 0 : _a[0]) || ((_b = event.touches) === null || _b === void 0 ? void 0 : _b[0]);
            if (!touch)
                return;
            x = touch.clientX;
            y = touch.clientY;
            // Check if this is a real tap (not a scroll gesture)
            if (touchStartX !== null &&
                touchStartY !== null &&
                touchStartTime !== null) {
                var deltaX = Math.abs(x - touchStartX);
                var deltaY = Math.abs(y - touchStartY);
                var duration = Date.now() - touchStartTime;
                // If the finger moved too much or the touch was too long, it's not a tap
                if (deltaX > TAP_DISTANCE_THRESHOLD ||
                    deltaY > TAP_DISTANCE_THRESHOLD ||
                    duration > TAP_DURATION_THRESHOLD) {
                    log('trackTap', "Ignoring touch - moved ".concat(deltaX.toFixed(0), "px, ").concat(deltaY.toFixed(0), "px over ").concat(duration, "ms"));
                    // Reset touch tracking
                    touchStartX = null;
                    touchStartY = null;
                    touchStartTime = null;
                    return;
                }
            }
            // Reset touch tracking
            touchStartX = null;
            touchStartY = null;
            touchStartTime = null;
        }
        else {
            x = event.clientX;
            y = event.clientY;
        }
        // Get document dimensions FIRST, before normalization
        // This ensures we use the exact same values for normalization and storage
        var scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        var scrollY = window.pageYOffset || document.documentElement.scrollTop;
        var scrollWidth = document.documentElement.scrollWidth ||
            document.body.scrollWidth ||
            window.innerWidth;
        var scrollHeight = document.documentElement.scrollHeight ||
            document.body.scrollHeight ||
            window.innerHeight;
        // Prevent division by zero
        if (scrollWidth === 0 || scrollHeight === 0) {
            return;
        }
        // Normalize using the same dimensions we'll store
        // This ensures consistency between normalized value and docWidth/docHeight
        var normalizedX = (x + scrollX) / scrollWidth;
        var normalizedY = (y + scrollY) / scrollHeight;
        var normalized = {
            x: Math.max(0, Math.min(1, normalizedX)),
            y: Math.max(0, Math.min(1, normalizedY)),
        };
        if (typeof window !== 'undefined' && window.optibaseDebugMode) {
            console.log('[Optibase Heatmap] Tap captured:', {
                'Client coordinates': { x: x, y: y },
                'Scroll position': { scrollX: scrollX, scrollY: scrollY },
                'Document dimensions': { width: scrollWidth, height: scrollHeight },
                'Normalized (0-1)': { x: normalized.x, y: normalized.y },
                Verification: {
                    'Should equal': {
                        x: (x + scrollX) / scrollWidth,
                        y: (y + scrollY) / scrollHeight,
                    },
                    'Calculated pixel position': {
                        x: normalized.x * scrollWidth,
                        y: normalized.y * scrollHeight,
                    },
                },
            });
        }
        heatmapData.taps.push({
            x: normalized.x,
            y: normalized.y,
            timestamp: Date.now() - pageStartTime,
            docWidth: scrollWidth,
            docHeight: scrollHeight,
        });
        log('trackTap', "Tap at (".concat(normalized.x.toFixed(2), ", ").concat(normalized.y.toFixed(2), ")"));
        // Check if we need to send data due to limits
        checkDataLimits();
    }
    function trackMovement(event) {
        var now = Date.now();
        if (now - lastMovementTime < movementThrottleDelay) {
            return;
        }
        lastMovementTime = now;
        var normalized = normalizeCoordinates(event.clientX, event.clientY);
        // Get document dimensions at capture time
        var scrollWidth = document.documentElement.scrollWidth ||
            document.body.scrollWidth ||
            window.innerWidth;
        var scrollHeight = document.documentElement.scrollHeight ||
            document.body.scrollHeight ||
            window.innerHeight;
        heatmapData.movement.push({
            x: normalized.x,
            y: normalized.y,
            timestamp: now - pageStartTime,
            docWidth: scrollWidth,
            docHeight: scrollHeight,
        });
        // Check if we need to send data due to limits (throttled check)
        // Only check every 100 points to avoid excessive checks
        if (heatmapData.movement.length % 100 === 0) {
            checkDataLimits();
        }
    }
    function startTracking() {
        // Track scroll events
        window.addEventListener('scroll', trackScroll, { passive: true });
        // Capture initial viewport (helps with sessions that never scroll)
        trackScroll();
        // Track clicks/taps
        // For touch devices, we need touchstart to detect scroll vs tap
        document.addEventListener('touchstart', trackTouchStart, { passive: true });
        document.addEventListener('click', trackTap, true);
        document.addEventListener('touchend', trackTap, true);
        // Track mouse movements
        document.addEventListener('mousemove', trackMovement, { passive: true });
        // Send data when page is about to unload
        // Use a wrapper to handle the event parameter
        window.addEventListener('beforeunload', function (event) {
            sendHeatmapDataWithEvent(event);
        });
        window.addEventListener('pagehide', function (event) {
            sendHeatmapDataWithEvent(event);
        });
        function sendHeatmapDataWithEvent(event) {
            // For beforeunload/pagehide, use synchronous approach with sendBeacon fallback
            var countPageview = !hasRecordedPageview;
            sendHeatmapDataSync(event, countPageview);
        }
        function sendHeatmapDataSync(event, countPageview) {
            if (countPageview === void 0) { countPageview = false; }
            if (isSendingData) {
                return;
            }
            if (!publicApiKey || !baseApiUrl) {
                return;
            }
            var testedUserId = window.optibaseTestedUserId;
            if (!testedUserId) {
                return;
            }
            var standaloneMatches = getStandaloneMatchesForCurrentUrl();
            if (standaloneMatches.length === 0) {
                return;
            }
            if (heatmapData.scroll.length === 0 &&
                heatmapData.taps.length === 0 &&
                heatmapData.movement.length === 0 &&
                !countPageview) {
                return;
            }
            var originUrl = window.location.origin + window.location.pathname;
            var timeOnPage = Math.floor((Date.now() - pageStartTime) / 1000);
            // For beforeunload events, prefer sendBeacon for reliability
            var useSendBeacon = event &&
                (event.type === 'beforeunload' || event.type === 'pagehide') &&
                typeof navigator !== 'undefined' &&
                navigator.sendBeacon;
            var createPayload = function () {
                var device = getCurrentDevice();
                var payload = {
                    publicApiKey: publicApiKey,
                    testedUserId: testedUserId,
                    originUrl: originUrl,
                    scroll: heatmapData.scroll,
                    taps: heatmapData.taps,
                    movement: heatmapData.movement,
                    countPageview: countPageview,
                    device: device,
                };
                if (countPageview) {
                    payload.timeOnPage = timeOnPage;
                }
                return payload;
            };
            var basePayload = createPayload();
            if (countPageview) {
                hasRecordedPageview = true;
            }
            // If a test variant is active on this page, include variant IDs for per-variant tracking
            // Otherwise, send without variant IDs (aggregate standalone data)
            var activeVariants = getTrackedVariants();
            var sendPayloadBeacon = function (payload) {
                if (useSendBeacon) {
                    var blob = new Blob([JSON.stringify(payload)], {
                        type: 'application/json',
                    });
                    navigator.sendBeacon("".concat(baseApiUrl, "/api/script/heatmap"), blob);
                }
                else {
                    fetch("".concat(baseApiUrl, "/api/script/heatmap"), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                        keepalive: true,
                    }).catch(function () { });
                }
            };
            if (activeVariants.length > 0) {
                // Send per-variant payloads with variant IDs
                activeVariants.forEach(function (variant) {
                    sendPayloadBeacon(__assign(__assign({}, basePayload), { userFriendlyTestId: variant.userFriendlyTestId, userFriendlyVariantId: variant.userFriendlyVariantId }));
                });
            }
            else {
                // No active test variant — send standalone without variant IDs
                sendPayloadBeacon(basePayload);
            }
        }
        // Also send data periodically to avoid data loss
        var intervalId = setInterval(function () {
            if (heatmapData.scroll.length > 0 ||
                heatmapData.taps.length > 0 ||
                heatmapData.movement.length > 0) {
                sendHeatmapData();
            }
        }, SEND_INTERVAL_MS);
        window.__optibaseHeatmapIntervalId = intervalId;
        log('startTracking', 'Started tracking heatmap data');
    }
    function sendHeatmapData() {
        return __awaiter(this, void 0, void 0, function () {
            var testedUserId, standaloneMatches, dataSnapshot, originUrl, basePayload, sendPromises, sendPayload, activeVariants, _i, activeVariants_1, variant, results, allSucceeded, error_1;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // Prevent concurrent sends
                        if (isSendingData) {
                            log('sendHeatmapData', 'Already sending data, skipping');
                            return [2 /*return*/];
                        }
                        if (!publicApiKey || !baseApiUrl) {
                            log('sendHeatmapData', 'Missing publicApiKey or baseApiUrl');
                            return [2 /*return*/];
                        }
                        testedUserId = window.optibaseTestedUserId;
                        if (!testedUserId) {
                            log('sendHeatmapData', 'No testedUserId available');
                            return [2 /*return*/];
                        }
                        standaloneMatches = getStandaloneMatchesForCurrentUrl();
                        if (standaloneMatches.length === 0) {
                            log('sendHeatmapData', 'No standalone heatmap matches for current URL');
                            return [2 /*return*/];
                        }
                        // Check if there's any data to send
                        if (heatmapData.scroll.length === 0 &&
                            heatmapData.taps.length === 0 &&
                            heatmapData.movement.length === 0) {
                            log('sendHeatmapData', 'No data to send');
                            return [2 /*return*/];
                        }
                        dataSnapshot = {
                            scroll: __spreadArray([], heatmapData.scroll, true),
                            taps: __spreadArray([], heatmapData.taps, true),
                            movement: __spreadArray([], heatmapData.movement, true),
                        };
                        isSendingData = true;
                        originUrl = window.location.origin + window.location.pathname;
                        basePayload = {
                            publicApiKey: publicApiKey,
                            testedUserId: testedUserId,
                            originUrl: originUrl,
                            scroll: dataSnapshot.scroll,
                            taps: dataSnapshot.taps,
                            movement: dataSnapshot.movement,
                            countPageview: false,
                            device: getCurrentDevice(),
                        };
                        sendPromises = [];
                        sendPayload = function (payload, label) { return __awaiter(_this, void 0, void 0, function () {
                            var response, error_2;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        log('sendHeatmapData', "Sending data for ".concat(label), {
                                            scrollPoints: dataSnapshot.scroll.length,
                                            tapPoints: dataSnapshot.taps.length,
                                            movementPoints: dataSnapshot.movement.length,
                                        });
                                        _a.label = 1;
                                    case 1:
                                        _a.trys.push([1, 3, , 4]);
                                        return [4 /*yield*/, fetch("".concat(baseApiUrl, "/api/script/heatmap"), {
                                                method: 'POST',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                },
                                                body: JSON.stringify(payload),
                                                keepalive: true,
                                            })];
                                    case 2:
                                        response = _a.sent();
                                        if (!response.ok) {
                                            throw new Error("HTTP error! status: ".concat(response.status));
                                        }
                                        return [2 /*return*/, true];
                                    case 3:
                                        error_2 = _a.sent();
                                        console.error("[Optibase Heatmap] Error sending heatmap data for ".concat(label, ":"), error_2);
                                        return [2 /*return*/, false];
                                    case 4: return [2 /*return*/];
                                }
                            });
                        }); };
                        activeVariants = getTrackedVariants();
                        if (activeVariants.length > 0) {
                            // Send per-variant payloads with variant IDs
                            for (_i = 0, activeVariants_1 = activeVariants; _i < activeVariants_1.length; _i++) {
                                variant = activeVariants_1[_i];
                                sendPromises.push(sendPayload(__assign(__assign({}, basePayload), { userFriendlyTestId: variant.userFriendlyTestId, userFriendlyVariantId: variant.userFriendlyVariantId }), "variant ".concat(variant.userFriendlyVariantId)));
                            }
                        }
                        else {
                            // No active test variant — send standalone without variant IDs
                            sendPromises.push(sendPayload(basePayload, 'standalone heatmap'));
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, Promise.allSettled(sendPromises)];
                    case 2:
                        results = _a.sent();
                        allSucceeded = results.every(function (result) { return result.status === 'fulfilled' && result.value === true; });
                        // Only clear data if all requests succeeded
                        if (allSucceeded) {
                            heatmapData = {
                                scroll: [],
                                taps: [],
                                movement: [],
                            };
                            log('sendHeatmapData', 'All data sent successfully, cleared local data');
                        }
                        else {
                            log('sendHeatmapData', 'Some requests failed, keeping data for retry');
                        }
                        return [3 /*break*/, 5];
                    case 3:
                        error_1 = _a.sent();
                        console.error('[Optibase Heatmap] Error in sendHeatmapData:', error_1);
                        return [3 /*break*/, 5];
                    case 4:
                        isSendingData = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    }
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize);
    }
    else {
        initialize();
    }
}
// Only initialize if heatmaps are enabled
// This will be checked by the main script before loading this script
OptibaseHeatmap();
export {};
